import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import documentService from '../services/documentService';
import DocumentPreview from '../components/documents/DocumentPreview';
import CodeMirror from '@uiw/react-codemirror';
import { markdown } from '@codemirror/lang-markdown';
import useNotification from '../hooks/useNotification';
// Import direct document handling functions
import { directUpdateDocument, downloadAsMarkdown, saveDocumentLocally } from '../utils/documentHelper';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import {
  PageContainer,
  Section,
  LoadingOverlay,
  EmptyState
} from '../design-system';

/**
 * DocumentDetail Page
 * Displays detailed view of a specific document with options to view and edit
 */
const DocumentDetail = () => {
  const { projectId, documentId } = useParams();
  const navigate = useNavigate();
  const [setProjectName, setDocumentName] = useOutletContext();
  const { showNotification } = useNotification();

  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [savingDocument, setSavingDocument] = useState(false);
  
  // Load document data
  useEffect(() => {
    const loadDocument = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const documentData = await documentService.getDocumentById(documentId);
        setDocument(documentData);
        
        // Set document name for breadcrumbs
        if (documentData?.title) {
          setDocumentName(documentData.title);
        }
      } catch (err) {
        console.error('Error loading document:', err);
        setError(err.message || 'Failed to load document');
      } finally {
        setLoading(false);
      }
    };
    
    loadDocument();
    
    return () => {
      // Clear document name when component unmounts
      setDocumentName('');
    };
  }, [documentId, setDocumentName]);
  
  const handleEdit = () => {
    // When edit mode is activated, set edit content to current document content
    if (document && document.content) {
      setEditContent(document.content);
    }
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    // Reset edit content when canceling
    setEditContent('');
    setEditMode(false);
  };

  const handleBack = () => {
    navigate(`/projects/${projectId}/documents`);
  };

  const handleWorkflow = () => {
    navigate(`/projects/${projectId}/prd-workflow/${documentId}`);
  };

  // Handle document content saving
  const handleSaveDocument = async () => {
    if (!editContent || editContent.trim() === '') {
      showNotification({
        message: "Cannot save empty content",
        type: "error"
      });
      return;
    }

    try {
      setSavingDocument(true);
      console.log("Saving document, content length:", editContent.length);

      // Save document locally as a backup first
      saveDocumentLocally(documentId, editContent);

      // Try to update the document using our direct utility only
      let updatedDocument = null;
      try {
        updatedDocument = await directUpdateDocument(documentId, editContent);
        console.log("Document updated successfully using direct method:", updatedDocument);
      } catch (directError) {
        console.error("Direct update failed:", directError);
        throw directError; // Re-throw to be caught by outer catch
      }

      // Create a new document object with updated content (in case API doesn't return full object)
      const newDocument = updatedDocument || {
        ...document,
        content: editContent,
        updatedAt: new Date().toISOString()
      };

      // Update local document state
      setDocument(newDocument);

      // Show success notification
      showNotification({
        message: "Document saved successfully",
        type: "success"
      });

      // Exit edit mode
      setEditMode(false);

    } catch (error) {
      console.error("Error saving document:", error);

      // Create a fallback solution - update the local state even though the server save failed
      try {
        // Create a fallback document with the updated content
        const fallbackDocument = {
          ...document,
          content: editContent,
          updatedAt: new Date().toISOString()
        };

        // Update local state so the user sees their changes
        setDocument(fallbackDocument);

        // Inform the user about the situation with option to manually download
        showNotification({
          message: `Server save failed, but your changes are shown locally. ${error.message}`,
          type: "warning",
          autoHideDuration: 8000  // Show longer
        });

        // Don't exit edit mode so user can try again or download manually
      } catch (localError) {
        showNotification({
          message: `Error saving document: ${error.message || "Unknown error"}`,
          type: "error"
        });
      }
    } finally {
      setSavingDocument(false);
    }
  };

  // Actions for the document
  const documentActions = document?.type === 'prd' ? (
    <Button 
      variant="contained"
      color="primary" 
      startIcon={<RestartAltIcon />} 
      onClick={handleWorkflow}
    >
      Continue to PRD Workflow
    </Button>
  ) : null;
  
  // Main navigation button
  const backButton = (
    <Button 
      startIcon={<ArrowBackIcon />} 
      onClick={handleBack}
    >
      Back to Documents
    </Button>
  );

  return (
    <PageContainer fullWidth maxWidth={false}>
      <Box sx={{
        mt: 0, // Remove top margin here since we'll set it in the inner container
        mb: 2, // Reduce bottom margin
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%'
      }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 4,
          pt: 2,
          pb: 2,
          px: 2,
          mt: 1, // Add top margin of 16px
          bgcolor: 'white',
          width: '100%'
        }}>
          {backButton}
          {documentActions}
        </Box>
        
        <Section elevation={1} sx={{ 
          bgcolor: 'white', 
          p: 4, 
          borderRadius: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          {loading ? (
            <LoadingOverlay message="Loading document..." />
          ) : error ? (
            <EmptyState
              title="Error Loading Document"
              description={error}
              actionText="Back to Documents"
              onAction={handleBack}
            />
          ) : document ? (
            <DocumentPreview
              document={document}
              onEdit={handleEdit}
            />
          ) : (
            <EmptyState
              title="Document Not Found"
              description="The requested document could not be found."
              actionText="Back to Documents"
              onAction={handleBack}
            />
          )}
        </Section>
      </Box>

      {/* Document Edit Dialog */}
      <Dialog
        open={editMode}
        onClose={handleCancelEdit}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            maxHeight: '900px',
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          pb: 2
        }}>
          <Typography variant="h6">
            Edit Document: {document?.title || ""}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Markdown format supported
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ py: 3 }}>
          <Box sx={{ height: 'calc(80vh - 200px)', width: '100%' }}>
            <CodeMirror
              value={editContent}
              height="100%"
              width="100%"
              onChange={(value) => setEditContent(value)}
              extensions={[markdown()]}
              placeholder="Enter markdown content here..."
              theme="light"
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{
          borderTop: '1px solid rgba(0, 0, 0, 0.12)',
          pt: 2,
          px: 3,
          justifyContent: 'flex-end'
        }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleCancelEdit}
            startIcon={<CancelIcon />}
            sx={{ mr: 2 }}
            disabled={savingDocument}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveDocument}
            startIcon={savingDocument ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={savingDocument || !editContent.trim()}
            sx={{ height: 40 }}
          >
            {savingDocument ? "Saving..." : "Save Document"}
          </Button>

          {/* Alternative download option in case API save fails */}
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              // Use our utility to download the document
              downloadAsMarkdown(editContent, document?.title || 'document');

              // Show notification
              showNotification({
                message: "Document downloaded as markdown file",
                type: "info"
              });
            }}
            sx={{ ml: 2, height: 40 }}
          >
            Download as Markdown
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default DocumentDetail;